/**
 * The sheet element's presentation: the host its `SheetPresentation` runs on
 * (`presentationHost`), navigation UI states, the reveal host the reveal
 * predicate reads, the loading spinner, and the scrub of the previous sheet's
 * visuals. Over the element's `SheetElementPort`; moved out of
 * sheet-element.ts (r5 FS 2, then the host). This module owns
 * `SheetPresentationState`: it is the only writer of the navigation reveal,
 * the overlay and what the reveal predicate reads.
 */
import { type Option, type SheetNetworkKey, type Unsubscribe } from "@tento-lona/sheets";
import { Tracing } from "@tento-core/tracing";
import { SheetCell } from "@tento-ui/components/sheet/sheet-cell";
import { LoadingIcon, LonaIcons } from "@tento-ui/ui/icons";
import { $$ } from "@tento-ui/fastdom";
import { BindGeneration, SettleDebt } from "@tento-scroll/engine/scroll-engine";
import { type SheetRevealInstrumentation } from "../instrumentation.ts";
import { sheetColumnsOf } from "./sheet-columns-of.ts";
import { SheetPresentation } from "./sheet-presentation.ts";
import type { SheetElementPort } from "./sheet-element-port.ts";
import { whenIncomingRender } from "./sheet-element-bind.ts";
import { revealReady } from "./sheet-reveal-ready.ts";
import type { SheetPresentationState } from "./sheet-element-state.ts";
import type { SheetElement } from "./sheet-element.ts";

/**
 * The element's `SheetPresentation.Host`, over its table: presents through
 * `presentSheet`, tells `listeners` each "ready" (`SheetElement.onReveal`)
 * with the surface bound and whether the page is in the foreground, judges
 * readiness by the reveal predicate (an exception is traced
 * `sheet/reveal-error` and rethrown, so the presentation fails rather than
 * reveals), re-checks on the scroll port's bind drain, mutates at the end of
 * the mutate phase, and schedules on the window's timers. Every member reads
 * the port when it runs, never when the host is built.
 */
export function presentationHost(
  port: SheetElementPort,
  listeners: ReadonlySet<(reveal: SheetElement.Reveal) => void>,
): SheetPresentation.Host {
  return {
    present: (state) => presentSheet(port, state),
    revealed: (how) =>
      notifyReveal(listeners, { how, sheet: port.core.sheet, foreground: document.visibilityState === "visible" }),
    ready: () => {
      try { return presentationReady(port); }
      catch (error) {
        const gen = port.presentationState.revealGeneration;
        Tracing.log("sheet/reveal-error", { gen, err: String(error) });
        throw error;
      }
    },
    subscribe: (callback) => port.$$.root.scrollPort.onBindDrain(callback),
    afterMutate: (callback) => $$.mutateEnd(callback),
    setTimeout: (callback, ms) => window.setTimeout(callback, ms),
    clearTimeout: (id) => window.clearTimeout(id),
  };
}

/** Whether the element behind `port` may reveal now: the reveal predicate (sheet-reveal-ready.ts) over its reveal host. */
export function presentationReady(port: SheetElementPort): boolean {
  return revealReady(revealHostOf(port));
}

/** A navigation's UI state (`SheetElement.NavigationUiState`). */
export type NavigationUiState =
  | { kind: "idle" }
  | { kind: "hidden_pending"; navId: number; title: string }
  | { kind: "spinner_pending"; navId: number; title: string }
  | { kind: "revealing"; navId: number; sheetId: SheetNetworkKey };

/** Apply a navigation's UI state to the element behind `port`. */
export function applyNavigationState(port: SheetElementPort, state: NavigationUiState): void {
  const ps = port.presentationState;
  const presentation = port.core.presentation;
  const bind = port.bindState;
  Tracing.log("sheet/nav-state", {
    kind: state.kind, sheetId: port.core.sheet?.handle.network.id ?? null, t: performance.now(),
  });
  const supersedesIdle = ps.navigationRevealController != null;
  ps.navigationRevealController?.abort();
  ps.navigationRevealController = null;
  if (state.kind === "hidden_pending" || state.kind === "spinner_pending") {
    if (!ps.navigationTransitionScrubbed) {
      ps.navigationPresentationTicket = presentation.start();
    }
    scrubPreviousSheetVisualState(port);
    ps.revealGeneration++;
    setNavigationHidden(port, true);
    setNavigationSpinner(port, state.kind === "spinner_pending");
    return;
  }
  if (state.kind === "revealing") {
    ps.revealGeneration++;
    return;
  }
  const scrubbed = ps.navigationTransitionScrubbed;
  ps.navigationTransitionScrubbed = false;
  if (supersedesIdle) ps.navigationPresentationTicket = presentation.start();
  const gen = ++ps.revealGeneration;
  const controller = new AbortController();
  ps.navigationRevealController = controller;
  const signal = controller.signal;
  const ticket = ps.navigationPresentationTicket != null &&
      presentation.current(ps.navigationPresentationTicket)
    ? ps.navigationPresentationTicket : presentation.hold();
  ps.navigationPresentationTicket = ticket;
  Tracing.log("sheet/idle-enter", { gen });
  const current = () => !signal.aborted && ps.revealGeneration === gen;
  const debt: SettleDebt = ps.scrubbedAtBindGeneration == null
    ? { kind: "none" }
    : { kind: "incurred-at", generation: ps.scrubbedAtBindGeneration };
  const anchor: Promise<string> = scrubbed && bind.lastRenderPromiseId <= ps.scrubRenderPromiseId
    ? new Promise((resolve) => {
      let finished = false;
      const release = (via: string) => {
        if (finished) return;
        finished = true;
        window.clearTimeout(timer);
        signal.removeEventListener("abort", cancel);
        dropWaiter();
        resolve(via);
      };
      const dropWaiter = whenIncomingRender(bind, () => release("render"));
      const cancel = () => release("cancelled");
      const timer = window.setTimeout(() => release("timeout"), 500);
      signal.addEventListener("abort", cancel, { once: true });
    }) : Promise.resolve("anchored");
  void anchor.then(async (via) => {
    if (!current() || via === "cancelled") return;
    if (via !== "anchored") {
      Tracing.log("sheet/reveal-anchor", { gen, via, renderId: bind.lastRenderPromiseId });
    }
    if (via === "timeout") {
      // No incoming render yet is slow, not failed: failing would fail open
      // onto the scrubbed previous sheet. Present "slow" and release the
      // hold; the late render's own bind hold decides the reveal. Until it
      // arrives, the stall rule presents "stalled-navigation" (cells hidden,
      // the spinner) rather than revealing the scrubbed previous sheet.
      presentation.awaitRender(ticket);
      const dropRenderWaiter = whenIncomingRender(bind, () => presentation.renderArrived(ticket));
      signal.addEventListener("abort", dropRenderWaiter, { once: true });
      presentation.exhausted(ticket);
      presentation.complete(ticket);
      return;
    }
    await bind.lastRenderPromise;
    if (!current()) return;
    const columns = port.core.layout.columns;
    ps.presentationStrip = columns.geometryBound ? columns.visibleIndices() : null;
    const result = await port.$$.root.scrollPort.whenSettled(debt, signal);
    if (!current() || result.kind === "cancelled") return;
    ps.presentationVia = result.via;
    if (result.kind === "exhausted") presentation.exhausted(ticket);
    presentation.complete(ticket);
  }).catch((error) => {
    if (!current()) return;
    Tracing.log("sheet/reveal-error", { gen, err: String(error) });
    presentation.fail(ticket);
  });
}

/** This element's state as the reveal predicate reads it (`revealReady`). */
export function revealHostOf(port: SheetElementPort): SheetRevealInstrumentation.Host {
  const ps = port.presentationState;
  const element = port.core.element;
  return {
    get sheet() {
      return port.core.sheet;
    },
    get columns() {
      return port.core.layout.columns;
    },
    bindQueue: () => port.$$.root.scrollPort.bindQueue(),
    get scrubbedAtBindGeneration() {
      return ps.scrubbedAtBindGeneration;
    },
    get isConnected() {
      return element.isConnected;
    },
    get offsetWidth() {
      return element.offsetWidth;
    },
    get offsetHeight() {
      return element.offsetHeight;
    },
    getBoundingClientRect: () => element.getBoundingClientRect(),
    get revealGeneration() {
      return ps.revealGeneration;
    },
    get presentationStrip() {
      return ps.presentationStrip;
    },
    // The reveal predicate (sheet-reveal-ready.ts) records the strip it judged here.
    recordStrip: (strip) => {
      ps.presentationStrip = strip;
    },
    get presentationVia() {
      return ps.presentationVia;
    },
  };
}

/** Record how a bind's settle was reached (`bindSheet`, through the port). */
export function recordSettle(ps: SheetPresentationState, via: string): void {
  ps.presentationVia = via;
}

/** Drop the navigation's hold and abort its reveal (recycle, disconnect). */
export function cancelNavigation(ps: SheetPresentationState): void {
  ps.navigationPresentationTicket = null;
  ps.navigationRevealController?.abort();
  ps.navigationRevealController = null;
}

/** A harness's reveal-contract debt, incurred at `generation` (`SheetRevealInstrumentation`). */
export function incurRevealDebt(ps: SheetPresentationState, generation: Option<BindGeneration>): void {
  ps.scrubbedAtBindGeneration = generation;
}

/** A harness's fresh reveal: no debt, no judged strip, no settle (`SheetRevealInstrumentation.seedLayout`). */
export function resetReveal(ps: SheetPresentationState): void {
  ps.scrubbedAtBindGeneration = null;
  ps.revealGeneration = 0;
  ps.presentationStrip = null;
  ps.presentationVia = null;
}

/**
 * What `presentSheet` writes of the sheet element: the host's
 * `data-presentation`, the content root's inertness, the root's hidden
 * state, the content viewport's visibility, and the navigation overlay.
 * `SheetElementPort` is one.
 */
export interface PresentSheetPort {
  readonly presentationState: SheetPresentationState;
  readonly core: {
    readonly element: HTMLElement;
    readonly layout: { readonly presentation: { setRootHidden(hidden: boolean): void } };
  };
  readonly $$: {
    readonly root: HTMLElement & {
      readonly scrollPort: { toggleContentVisibility(force: Option<boolean>, animate: boolean): void };
    };
    readonly navigationLoadingOverlay: HTMLElement;
  };
}

/** Show the presentation state on the element behind `port`. */
export function presentSheet(port: SheetElementPort, state: SheetPresentation.State): void {
  presentSheetState(port, state);
}

/** `SheetElement.onReveal`: `listener` joins `listeners` until the returned `Unsubscribe` is called. */
export function listenReveal<Reveal>(listeners: Set<(reveal: Reveal) => void>, listener: (reveal: Reveal) => void): Unsubscribe {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Tell every listener one "ready" presentation, after its mutation (over a copy: a listener may unsubscribe mid-call). */
export function notifyReveal<Reveal>(listeners: ReadonlySet<(reveal: Reveal) => void>, reveal: Reveal): void {
  for (const listener of [...listeners]) listener(reveal);
}

/**
 * Show the presentation state (`data-presentation`, the spinner). "slow" and
 * "failed" are telemetry only, with no notice and no Retry (operator
 * 2026-10-09: "never show the affordance", then "Remove it too"). "slow"
 * leaves the overlay as "loading" left it; "failed" fails open: the overlay
 * is cleared and what is bound is shown, interactive. "stalled-navigation"
 * shows the layout, whose header band is the skeleton, with the content
 * viewport (the cells and their contents) hidden, the root inert and the
 * loading spinner (operator 2026-10-09: "hide the cell and its contents and
 * provide a basic loading spinner").
 */
export function presentSheetState(port: PresentSheetPort, state: SheetPresentation.State): void {
  const ps = port.presentationState;
  const element = port.core.element;
  element.setAttribute("data-presentation", state);
  const revealed = state === "ready" || state === "failed";
  port.$$.root.inert = !revealed;
  port.$$.root.setAttribute("aria-hidden", String(!revealed));
  // The content root's inert state owns both pointer and keyboard isolation.
  // A redundant non-interactable host attribute also invalidates generic
  // #root selectors across nested shadow trees and inherits pointer-events
  // through every cell.
  setNavigationHidden(port, !revealed && state !== "stalled-navigation");
  port.$$.root.scrollPort.toggleContentVisibility(revealed, true);
  element.toggleAttribute("legend-ready", revealed);
  if (state === "ready") {
    Tracing.log("sheet/reveal", { waitMs: performance.now() - ps.presentationStartedAt, gen: ps.revealGeneration });
    setNavigationSpinner(port, false);
    return;
  }
  if (state === "loading") {
    ps.presentationStartedAt = performance.now();
    ps.presentationStrip = null;
    ps.presentationVia = null;
    setNavigationSpinner(port, true);
    return;
  }
  if (state === "slow") {
    Tracing.log("sheet/reveal-slow", { gen: ps.revealGeneration, waitMs: performance.now() - ps.presentationStartedAt });
    return;
  }
  if (state === "stalled-navigation") {
    setNavigationSpinner(port, true);
    return;
  }
  setNavigationSpinner(port, false);
  Tracing.log("sheet/reveal-recovery", { gen: ps.revealGeneration, reason: state });
}

function scrubPreviousSheetVisualState(port: SheetElementPort): void {
  const ps = port.presentationState;
  const layout = port.core.layout;
  if (ps.navigationTransitionScrubbed) return;
  ps.navigationTransitionScrubbed = true;
  // Incur the reveal debt at a known generation: the scrub zeroes heights on
  // the promise that binds run AFTER this point restore them, so the reveal
  // must not release on any completion that predates this snapshot.
  ps.scrubbedAtBindGeneration =
    port.$$.root.scrollPort.bindQueue().current;
  ps.scrubRenderPromiseId = port.bindState.lastRenderPromiseId;

  port.defaultBinder.clearRowModes?.();
  layout.children.fixed.dismissAll();
  layout.children.static.dismissAll();
  port.forgetStaticChildren();
  port.forgetSpecGenerations();
  port.fixedChildrenLifetime.retire();

  for (const el of layout.columns.renderedElements()) {
    const { $header, $body, $footer } = sheetColumnsOf(el);
    const $cells: SheetCell[] = [
      ...$header.$cells(),
      ...$body.$cells(),
      ...($footer?.$cells() ?? []),
    ];
    for (const $cell of $cells) {
      $cell.bindHeight(0);
    }
  }

  layout.bindScrollHeightOnly(0);
}

function setNavigationHidden(port: Pick<PresentSheetPort, "core">, hidden: boolean): void {
  port.core.layout.presentation.setRootHidden(hidden);
}

function setNavigationSpinner(port: Pick<PresentSheetPort, "presentationState" | "$$">, shown: boolean): void {
  const ps = port.presentationState;
  port.$$.navigationLoadingOverlay.toggleAttribute("shown", shown);
  if (!shown) {
    port.$$.navigationLoadingOverlay.replaceChildren();
    ps.navigationSpinnerEl = null;
    return;
  }

  if (ps.navigationSpinnerEl) return;
  const $spinner = LonaIcons.make(LoadingIcon, { sizePx: 18 });
  $spinner.setAttribute("aria-label", "Loading");
  $spinner.setAttribute("role", "status");
  port.$$.navigationLoadingOverlay.replaceChildren($spinner);
  ps.navigationSpinnerEl = $spinner;
}
